// DocumentAPI.ts - API layer for DMS document operations
// Now uses shared document types and services

import { useKeycloak } from '@react-keycloak/web';
import { useMemo } from 'react';
import { 
  AITagSuggestion, 
  AIRelatedDocument, 
  AIConfidencePreview, 
  MCPProcessingProfile, 
  Jurisdiction, 
  DocumentPurpose, 
  EnhancedDocumentFormData 
} from './types/DocumentTypes';

// Import shared types for compatibility (used in type definitions)
// These types are used in the DocumentListItem interface below
// (Removed unused import)

// DMS-specific document types (compatible with shared types)
export interface DocumentListItem {
  document_id: string;
  id?: string; // Optional, for compatibility
  title: string;
  type: string;
  size: number;
  created_at: string;
  updated_at: string;
  effective_modified_at?: string;
  status: 'Active' | 'Archived' | 'deleted' | 'processing' | 'needs_review' | 'ai_modified';
  keep_visible?: boolean | null;
  client_name?: string;
  client_id?: string;
  matter_description?: string;
  matter_id?: string;
  created_by_name?: string;
  created_by?: string;
  created_by_user_id?: string; // For checkout status filtering
  latest_version?: {
    version_id: string;
    version_number: number | string;
    original_name: string;
    mime_type: string;
    uploaded_at?: string;
    updated_at?: string;
    checked_in_at?: string | null;
    changes_description?: string | null;
    checkin_metadata?: Record<string, any> | null;
  };
  // Document Category fields
  category_id?: string;
  category_name?: string;
  category_description?: string;
  category_type?: string;
  // Canonical legal document taxonomy fields
  primary_type?: string | null;
  sub_type?: string | null;
  matter_phase?: string | null;
  classification_version?: string | null;
  classification_confidence?: number | string | null;
  classification_method?: string | null;
  last_classified_at?: string | null;
  // New AI and collaboration fields
  ai_metadata?: {
    extracted_fields?: any[];
    classification?: { practice_area: string; tags: string[] };
    citations?: any[];
    processing_status?: 'completed' | 'pending' | 'failed';
  };
  shared_with?: string[];
  permissions?: { [userId: string]: 'read' | 'edit' | 'comment' };
  realm_id: string;
  // UX optimization fields
  document_preview_url?: string;
  edit_history?: any[];
  document_tags?: string[];
  external_reference_id?: string;
  last_accessed_at?: string;
  // Check-in/Check-out Workflow Fields
  is_checked_out?: boolean;
  checked_out_by_user_id?: string | null;
  checked_out_at?: string | null;
  checked_out_expires_at?: string | null;
  working_copy_content?: string | null;
  working_copy_file_path?: string | null;
  last_local_save_at?: string | null;
  working_copy_metadata?: {
    last_autosave_at?: string;
    autosave_count?: number;
    session_id?: string;
    device_info?: string;
    unsaved_changes?: boolean;
  } | null;
}

export interface DocumentMetadata {
  key: string;
  value: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentDuplicateCheckResponse {
  isDuplicate: boolean;
  existingDocumentId?: string;
  existingVersionId?: string;
  existingFileName?: string;
  existingDocumentTitle?: string;
  existingClientId?: string | null;
  existingClientName?: string | null;
  existingMatterId?: string | null;
  existingMatterNumber?: string | null;
  similarityScore?: number;
  message?: string;
}

export interface DocumentRenderingGuarantee {
  originalFilePreserved: true;
  exactLoadUsesOriginalBytes: true;
  previewBehavior: 'original_exact' | 'derived_preview_original_preserved';
  previewMessage: string;
}

interface DocumentIngestBaseResult {
  file: {
    fileName: string;
    fileSize: number;
    mimeType: string;
    extension: string;
    sha256: string;
    format: {
      key: string;
      label: string;
      extensions: string[];
      mimeTypes: string[];
      previewBehavior: 'original_exact' | 'derived_preview_original_preserved';
    };
  };
  duplicate: DocumentDuplicateCheckResponse;
  suggestions: {
    title: string;
    description: string;
    organization?: {
      primaryType: string;
      subType?: string;
      phase: string;
      categoryLabel: string;
      filingStatus: string;
      sourceRole: string;
      documentDate?: string;
      filedDate?: string;
      servedDate?: string;
      receivedDate?: string;
      confidence: number;
      method: string;
      tags: string[];
      rationale: string;
    };
    confidence: {
      title: number;
      description: number;
    };
  };
  rendering: DocumentRenderingGuarantee;
}

export interface DocumentIngestAnalysis extends DocumentIngestBaseResult {
  intent: 'analyze';
}

export interface DocumentIngestResult extends DocumentIngestBaseResult {
  intent: 'ingest';
  document: {
    documentId: string;
    versionId: string;
    title: string;
    clientId?: string | null;
    matterId?: string | null;
  };
}

// ============================================================================
// Document Version Edit Status Types (Frontend)
// ============================================================================
export enum DocumentEditStatus {
  ACTIVE = 'active',
  LOCKED = 'locked', 
  ARCHIVED = 'archived'
}

export interface DocumentDetail extends DocumentListItem {
  versions: DocumentVersion[];
  extracted_text?: string;
  ai_insights?: any;
  metadata?: Record<string, any>;
  content?: string;
  content_format: 'html' | 'markdown' | 'lexical' | 'plaintext';
}

export interface SignatureAuditEvidenceMetadata {
  schema_version?: string;
  evidence_hash?: string;
  event_chain_valid?: boolean;
  event_chain_head_hash?: string;
  export_endpoint?: string;
  generated_at?: string;
}

export interface SignatureAuditSignerSummaryEntry {
  user_id?: string | null;
  display_name?: string;
  field_count?: number;
}

export interface DocumentVersion {
  version_id: string;
  document_id: string;
  source_document_id: string; // New field for DAG workflows
  version_number: string;
  changes_description?: string;
  file_path: string;
  file_id: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  file_hash: string;
  storage_provider: string;
  uploaded_by_user_id: string;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
  realm_id: string;
  
  // ============================================================================
  // Document Version Edit Status (Immutable Version Control)
  // ============================================================================
  edit_status: DocumentEditStatus; // Controls whether this version can be modified
  
  // Check-in/Check-out Workflow Metadata
  checked_in_by_user_id?: string | null;
  checked_in_at?: string | null;
  version_comment?: string | null;
  is_promoted?: boolean;
  promoted_at?: string | null;
  promoted_by_user_id?: string | null;
  parent_version_id?: string | null;
  checkin_metadata?: {
    checkout_duration_hours?: number;
    working_copy_saved?: boolean;
    autosave_count?: number;
    session_id?: string;
    device_info?: string;
    force_checkin?: boolean;
    force_checkin_reason?: string;
    force_checkin_by?: string;
    tracking_summary?: Record<string, any>;
    signature_audit?: {
      session_id?: string;
      committed_at?: string;
      policy_profile_id?: string | null;
      policy_profile_key?: string | null;
      evidence?: SignatureAuditEvidenceMetadata;
      signer_summary?: {
        signed_users?: SignatureAuditSignerSummaryEntry[];
        assigned_users?: SignatureAuditSignerSummaryEntry[];
        signed_field_count?: number;
      };
      composition?: Record<string, unknown>;
      validation?: Record<string, unknown>;
    } & Record<string, unknown>;
    pdf_edit_audit?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface DocumentCitationOccurrence {
  occurrence_id: string;
  occurrence_index?: number | null;
  start_offset?: number | null;
  end_offset?: number | null;
  context_snippet?: string | null;
  section_label?: string | null;
  page_number?: number | null;
  confidence?: number | null;
}

export interface DocumentCitationRecord {
  citation_id: string;
  citation_text: string;
  normalized_citation_text?: string;
  authority_type?: string;
  jurisdiction?: string | null;
  pin_cite?: string | null;
  year?: number | null;
  confidence?: number | null;
  extraction_method?: string;
  validation_status?: string;
  occurrence_count?: number;
  first_start_offset?: number | null;
  last_end_offset?: number | null;
  llm_enrichment_status?: string;
  metadata?: Record<string, any> | null;
  occurrences?: DocumentCitationOccurrence[];
}

export interface DocumentVersionCitationsResponse {
  document_id: string;
  version_id: string;
  title?: string | null;
  citations: DocumentCitationRecord[];
  citation_count: number;
  occurrence_count: number;
}

// Type-specific response interfaces
export interface DocumentListResponse {
  documents: DocumentListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  realm_id: string;
}

export interface DocumentDetailResponse {
  document: DocumentDetail;
}

export interface DocumentVersionResponse {
  version: DocumentVersion;
}

export interface DocumentVersionsResponse {
  versions: DocumentVersion[];
  total: number;
  source_document_id: string;
}

export interface DocumentSearchResponse {
  results: DocumentListItem[];
  total: number;
  query: string;
  search_type: 'keyword' | 'semantic' | 'hybrid';
  relevance_scores?: number[];
  ai_suggestions?: string[];
  page: number;
  limit: number;
}

export interface DocumentFilterResponse {
  documents: DocumentListItem[];
  total: number;
  filters: {
    applied: any;
    available: any;
    ai_suggestions?: string[];
  };
  page: number;
  limit: number;
}

export interface DocumentAnalyticsResponse {
  analytics: {
    total_documents: number;
    documents_by_type: Record<string, number>;
    documents_by_status: Record<string, number>;
    recent_uploads: number;
    storage_used: number;
    ai_processed: number;
    practice_area_breakdown: Record<string, number>;
    collaboration_metrics: {
      shared_documents: number;
      active_collaborations: number;
    };
    ai_insights: {
      risk_assessment: string;
      compliance_alerts: string[];
      efficiency_suggestions: string[];
    };
    // 🆕 NEW: Enhanced Classification Analytics
    classification_analytics: {
      total_classified: number;
      classification_accuracy: number;
      classification_methods: Record<string, number>;
      document_types_distribution: Record<string, number>;
      document_subtypes_distribution: Record<string, number>;
      language_distribution: Record<string, number>;
      phase_distribution: Record<string, number>;
      confidence_distribution: {
        high: number;
        medium: number;
        low: number;
      };
      multilingual_metrics: {
        total_multilingual: number;
        languages_detected: string[];
        translation_required: number;
        auto_translation_applied: number;
      };
      processing_performance: {
        average_processing_time_ms: number;
        total_processing_time_ms: number;
        classification_success_rate: number;
        language_detection_success_rate: number;
      };
      workflow_analytics: {
        templates_generated: number;
        workflows_triggered: number;
        matter_assignments: number;
        party_extractions: number;
      };
      ai_enhancements: {
        party_entities_extracted: number;
        jurisdiction_analysis: number;
        risk_assessments: number;
        compliance_checks: number;
      };
    };
  };
}

export interface DocumentProcessingResponse {
  success: boolean;
  processing_id: string;
  status: string;
  ai_tasks: {
    ocr: boolean;
    classification: boolean;
    citation_extraction: boolean;
    risk_assessment: boolean;
  };
}

export interface DocumentProcessingStatusResponse {
  processing_status: {
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress: number;
    message?: string;
    ai_tasks: {
      ocr: { status: string; progress: number };
      classification: { status: string; progress: number };
      citation_extraction: { status: string; progress: number };
      risk_assessment: { status: string; progress: number };
    };
  };
}

/**
 * Phase 5 — unified ingest-lifecycle stage returned alongside the legacy
 * status fields. The modal keys CTA gating off `lifecycle.is_searchable`
 * (which is only true when `lifecycle.stage === 'searchable'`).
 */
export type IngestLifecycleStage =
  | 'uploaded'
  | 'extracting'
  | 'derived_text_ready'
  | 'derived_text_failed'
  | 'searchable'
  | 'blocked_no_matter'
  | 'unknown';

export interface EdgeCiteAnalysisResponse {
  success: boolean;
  status: 'queued' | string;
  provider: 'edgecite_weaviate';
  job_id: string;
  document_id: string;
  version_id: string;
  message: string;
}

export interface EdgeCiteAnalysisStep {
  key: string;
  label: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | string;
  progress: number;
  message: string | null;
  error_message: string | null;
  results: Record<string, any> | null;
  started_at: string | null;
  completed_at: string | null;
  processing_time_ms: number | null;
  updated_at: string | null;
}

export interface EdgeCiteAnalysisStatusResponse {
  provider: 'edgecite_weaviate';
  document_id: string;
  version_id: string | null;
  job_id: string | null;
  status: 'not_started' | 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  document: {
    title: string | null;
    matter_id: string | null;
    auto_population_status: string | null;
    edgecite_weaviate_analysis: Record<string, any> | null;
  } | null;
  steps: EdgeCiteAnalysisStep[];
  insight: {
    insight_id: string;
    summary_text: string | null;
    data: Record<string, any> | null;
    source_model_version_tag: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  feed: Array<{
    timestamp: string;
    label: string;
    status: string;
  }>;
}

const extractApiErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.clone().json();
    if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
    if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error;
    if (typeof payload?.description === 'string' && payload.description.trim()) return payload.description;
  } catch {
    // Fall through to the status text below.
  }
  return `${fallback}: ${response.statusText || response.status}`;
};

export interface DocumentListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'title' | 'created_at' | 'updated_at' | 'size' | 'type';
  sortOrder?: 'asc' | 'desc';
  status?: 'Active' | 'Archived' | 'deleted' | 'processing' | 'needs_review' | 'ai_modified';
  fileType?: string;
  clientId?: string;
  matterId?: string;
  dateFrom?: string;
  dateTo?: string;
  categoryId?: string;
  document_type?: string;
  document_subtype?: string;
  language?: string;
  phase?: string;
  practice_area?: string;
  jurisdiction?: string;
  urgency?: string;
  classification_method?: string;
  date_range?: string;
  confidence_min?: number;
  confidence_max?: number;
  realmId?: string;
  tagsAny?: string[];
  tagsAll?: string[];
  excludeTags?: string[];
  // NEW: Assignment status filtering
  assignmentStatus?: 'all' | 'assigned' | 'unassigned' | 'no_client' | 'no_matter';
}

export interface DMSFilterOption {
  value: string;
  label: string;
  categoryType?: string;
  practiceAreas?: string[];
  jurisdictions?: string[];
  defaultStage?: string;
}

export interface DMSFilterOptionsResponse {
  practiceAreas: DMSFilterOption[];
  jurisdictions: DMSFilterOption[];
  documentTypes: DMSFilterOption[];
  languages: DMSFilterOption[];
  phases: DMSFilterOption[];
  urgencyLevels: DMSFilterOption[];
  classificationMethods: DMSFilterOption[];
}

export interface DocumentSearchParams {
  q: string;
  type?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  semantic?: boolean;
  practice_area?: string;
  tags_any?: string[];
  tags_all?: string[];
  exclude_tags?: string[];
  page?: number;
  limit?: number;
}

export interface DocumentFilterParams {
  file_type?: string;
  status?: string;
  created_by?: string;
  date_range?: string;
  practice_area?: string;
  ai_classification?: string;
  tags_any?: string[];
  tags_all?: string[];
  exclude_tags?: string[];
  page?: number;
  limit?: number;
}

export interface DocumentAnalyticsParams {
  date_range?: string;
  practice_area?: string;
}

export interface DocumentProcessingParams {
  ai_tasks: {
    ocr: boolean;
    classification: boolean;
    citation_extraction: boolean;
    risk_assessment: boolean;
  };
}

// Utility to extract realmId from Keycloak token
function extractRealmIdFromToken(tokenParsed: any): string {
  // Try to get realm from a custom claim, else parse from issuer URL
  if (tokenParsed.realm) return tokenParsed.realm;
  if (tokenParsed.iss) {
    // Example: 'https://auth.webelievesoftware.com/auth/realms/firmname-realm'
    const match = tokenParsed.iss.match(/\/realms\/([^/]+)/);
    if (match) return match[1];
  }
  throw new Error('Unable to extract realmId from token');
}

// Hook for API calls with authentication
export const useDocumentAPI = () => {
  const { keycloak } = useKeycloak();
  const realmId = extractRealmIdFromToken(keycloak.tokenParsed);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${keycloak.token}`,
    'Content-Type': 'application/json',
  });

  const baseUrl = window.location.origin;

  // Runtime check for realmId consistency
  function checkRealmId(paramRealmId?: string) {
    if (paramRealmId && paramRealmId !== realmId) {
      throw new Error(`Security violation: realmId mismatch (token: ${realmId}, param: ${paramRealmId})`);
    }
    return realmId;
  }

  // List documents with filtering, pagination, and sorting
  const listDocuments = async (params: DocumentListParams = {}): Promise<DocumentListResponse> => {
    checkRealmId(params && (params as any).realmId);
    const searchParams = new URLSearchParams();
    // Always include realmId
    searchParams.append('realmId', realmId);
    // Add pagination
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    // Add filters
    if (params.search) searchParams.append('search', params.search);
    if (params.clientId) searchParams.append('client_id', params.clientId);
    if (params.matterId) searchParams.append('matter_id', params.matterId);
    if (params.status) searchParams.append('status', params.status);
    if (params.fileType) searchParams.append('mime_type', params.fileType);
    if (params.dateFrom) searchParams.append('created_after', params.dateFrom);
    if (params.dateTo) searchParams.append('created_before', params.dateTo);
    if (params.categoryId) searchParams.append('category_id', params.categoryId);
    if (params.document_type) searchParams.append('document_type', params.document_type);
    if (params.document_subtype) searchParams.append('document_subtype', params.document_subtype);
    if (params.language) searchParams.append('language', params.language);
    if (params.phase) searchParams.append('phase', params.phase);
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);
    if (params.jurisdiction) searchParams.append('jurisdiction', params.jurisdiction);
    if (params.urgency) searchParams.append('urgency', params.urgency);
    if (params.classification_method) searchParams.append('classification_method', params.classification_method);
    if (params.date_range) searchParams.append('date_range', params.date_range);
    if (typeof params.confidence_min === 'number') searchParams.append('confidence_min', params.confidence_min.toString());
    if (typeof params.confidence_max === 'number') searchParams.append('confidence_max', params.confidence_max.toString());
    params.tagsAny?.forEach((tag) => searchParams.append('tags_any', tag));
    params.tagsAll?.forEach((tag) => searchParams.append('tags_all', tag));
    params.excludeTags?.forEach((tag) => searchParams.append('exclude_tags', tag));
    if (params.sortBy) searchParams.append('sort_by', params.sortBy);
    if (params.sortOrder) searchParams.append('sort_order', params.sortOrder);
    
    // NEW: Assignment status filtering
    if (params.assignmentStatus) {
      searchParams.append('assignment_status', params.assignmentStatus);
    }

    const response = await fetch(`${baseUrl}/api/v1/dms/documents?${searchParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch documents: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.documents) {
      return data as DocumentListResponse;
    } else {
      // Fallback for old response format
      return {
        documents: Array.isArray(data) ? data : [],
        total: Array.isArray(data) ? data.length : 0,
        page: 1,
        limit: Array.isArray(data) ? data.length : 0,
        totalPages: 1,
        realm_id: realmId
      };
    }
  };

  const labelFromValue = (value: string): string =>
    value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const normalizeToken = (value?: string): string =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const overlapsConfiguredScope = (configured: string[], categoryValues?: string[]): boolean => {
    if (!categoryValues || categoryValues.length === 0) return true;
    if (!configured.length) return true;
    if (categoryValues.some((value) => ['general', 'general_practice', 'all'].includes(normalizeToken(value)))) return true;
    const configuredSet = new Set(configured.map(normalizeToken));
    return categoryValues.some((value) => configuredSet.has(normalizeToken(value)));
  };

  const getDMSFilterOptions = async (): Promise<DMSFilterOptionsResponse> => {
    const [firmResponse, categoryResponse] = await Promise.all([
      fetch(`${baseUrl}/api/v1/dms/practice-areas/firm-specific`, {
        headers: getAuthHeaders(),
      }),
      fetch(`${baseUrl}/api/v1/dms/categories?realmId=${realmId}`, {
        headers: getAuthHeaders(),
      }),
    ]);

    if (!firmResponse.ok) {
      throw new Error(`Failed to fetch firm filter settings: ${firmResponse.statusText}`);
    }
    if (!categoryResponse.ok) {
      throw new Error(`Failed to fetch document categories: ${categoryResponse.statusText}`);
    }

    const firmData = await firmResponse.json();
    const categoryData = await categoryResponse.json();
    const practiceAreaValues: string[] = firmData.practiceAreas || [];
    const jurisdictionValues: string[] = firmData.jurisdictions || firmData.licensedStates || [];
    const rawCategories = categoryData.categories || categoryData.data || [];
    const categories = rawCategories.filter((category: any) =>
      category?.is_active !== false &&
      overlapsConfiguredScope(practiceAreaValues, category.practice_areas) &&
      overlapsConfiguredScope(jurisdictionValues, category.jurisdictions)
    );

    const uniqueStages = Array.from(new Set([
      'intake',
      'review',
      'approved',
      'active',
      'closed',
      'rejected',
      ...categories.map((category: any) => category.default_stage).filter(Boolean),
    ]));

    return {
      practiceAreas: practiceAreaValues.map((value) => ({ value, label: labelFromValue(value) })),
      jurisdictions: jurisdictionValues.map((value) => ({ value, label: value.toUpperCase() })),
      documentTypes: categories.map((category: any) => ({
        value: category.id,
        label: category.name,
        categoryType: category.category_type,
        practiceAreas: category.practice_areas || [],
        jurisdictions: category.jurisdictions || [],
        defaultStage: category.default_stage,
      })),
      languages: [
        { value: 'en', label: 'English' },
        { value: 'es', label: 'Spanish' },
        { value: 'fr', label: 'French' },
        { value: 'de', label: 'German' },
        { value: 'it', label: 'Italian' },
        { value: 'pt', label: 'Portuguese' },
      ],
      phases: uniqueStages.map((value) => ({ value: String(value), label: labelFromValue(String(value)) })),
      urgencyLevels: ['low', 'medium', 'high', 'urgent'].map((value) => ({ value, label: labelFromValue(value) })),
      classificationMethods: ['manual', 'rule_based', 'machine_learning', 'hybrid', 'word_addin'].map((value) => ({
        value,
        label: labelFromValue(value),
      })),
    };
  };

  // Get single document with all details
  const getDocument = async (documentId: string, paramRealmId?: string): Promise<DocumentDetail> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}?realmId=${realmId}&versions=all`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.document) {
      return data.document as DocumentDetail;
    } else {
      // Fallback for old response format
      return data as DocumentDetail;
    }
  };

  // Search documents with semantic search support
  const searchDocuments = async (params: DocumentSearchParams): Promise<DocumentSearchResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('q', params.q);
    if (params.type) searchParams.append('type', params.type);
    if (params.status) searchParams.append('status', params.status);
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    if (params.semantic) searchParams.append('semantic', params.semantic.toString());
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);
    params.tags_any?.forEach((tag) => searchParams.append('tags_any', tag));
    params.tags_all?.forEach((tag) => searchParams.append('tags_all', tag));
    params.exclude_tags?.forEach((tag) => searchParams.append('exclude_tags', tag));
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const response = await fetch(`${baseUrl}/api/v1/dms/documents/search?${searchParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to search documents: ${response.statusText}`);
    }

    return response.json();
  };

  // Filter documents by various criteria
  const filterDocuments = async (params: DocumentFilterParams): Promise<DocumentFilterResponse> => {
    const searchParams = new URLSearchParams();
    if (params.file_type) searchParams.append('file_type', params.file_type);
    if (params.status) searchParams.append('status', params.status);
    if (params.created_by) searchParams.append('created_by', params.created_by);
    if (params.date_range) searchParams.append('date_range', params.date_range);
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);
    if (params.ai_classification) searchParams.append('ai_classification', params.ai_classification);
    params.tags_any?.forEach((tag) => searchParams.append('tags_any', tag));
    params.tags_all?.forEach((tag) => searchParams.append('tags_all', tag));
    params.exclude_tags?.forEach((tag) => searchParams.append('exclude_tags', tag));
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const response = await fetch(`${baseUrl}/api/v1/dms/documents/filter?${searchParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to filter documents: ${response.statusText}`);
    }

    return response.json();
  };

  // Get document analytics
  const getDocumentAnalytics = async (params: DocumentAnalyticsParams = {}): Promise<DocumentAnalyticsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.date_range) searchParams.append('date_range', params.date_range);
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);

    const response = await fetch(`${baseUrl}/api/v1/dms/documents/analytics?${searchParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.statusText}`);
    }

    return response.json();
  };

  // 🆕 NEW: Enhanced Analytics and Reporting APIs ---

  // Get comprehensive analytics report
  const getComprehensiveAnalyticsReport = async (params: {
    date_range?: string;
    practice_area?: string;
    include_trends?: boolean;
    include_recommendations?: boolean;
  } = {}): Promise<{
    success: boolean;
    report: any;
    generated_at: string;
    realm_id: string;
  }> => {
    const searchParams = new URLSearchParams();
    if (params.date_range) searchParams.append('date_range', params.date_range);
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);
    if (params.include_trends !== undefined) searchParams.append('include_trends', params.include_trends.toString());
    if (params.include_recommendations !== undefined) searchParams.append('include_recommendations', params.include_recommendations.toString());

    const response = await fetch(`${baseUrl}/api/v1/dms/documents/analytics/comprehensive-report?${searchParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch comprehensive analytics report: ${response.statusText}`);
    }

    return response.json();
  };

  // Get real-time analytics
  const getRealTimeAnalytics = async (): Promise<{
    success: boolean;
    real_time_analytics: {
      current_processing: number;
      queue_length: number;
      recent_classifications: number;
      system_health: 'excellent' | 'good' | 'fair' | 'poor';
      alerts: string[];
    };
    timestamp: string;
    realm_id: string;
  }> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/analytics/real-time`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch real-time analytics: ${response.statusText}`);
    }

    return response.json();
  };

  // Get analytics dashboard summary
  const getAnalyticsDashboardSummary = async (params: {
    date_range?: string;
    practice_area?: string;
  } = {}): Promise<{
    success: boolean;
    dashboard_summary: any;
    generated_at: string;
    realm_id: string;
  }> => {
    const searchParams = new URLSearchParams();
    if (params.date_range) searchParams.append('date_range', params.date_range);
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);

    const response = await fetch(`${baseUrl}/api/v1/dms/documents/analytics/dashboard-summary?${searchParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics dashboard summary: ${response.statusText}`);
    }

    return response.json();
  };

  // Export analytics report
  const exportAnalyticsReport = async (params: {
    format: 'json' | 'csv' | 'pdf';
    date_range?: string;
    include_trends?: boolean;
    include_recommendations?: boolean;
    practice_area?: string;
  }): Promise<Blob> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/analytics/export`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Failed to export analytics report: ${response.statusText}`);
    }

    return response.blob();
  };

  // --- END ENHANCED ANALYTICS APIs ---

  // Process document with AI tasks
  const processDocument = async (documentId: string, params: DocumentProcessingParams): Promise<DocumentProcessingResponse> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/process`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Failed to process document: ${response.statusText}`);
    }

    return response.json();
  };

  const triggerEdgeCiteDocumentAnalysis = async (
    documentId: string,
    options: { versionId?: string } = {}
  ): Promise<EdgeCiteAnalysisResponse> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/edgecite-analysis`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response, 'Failed to queue EdgeCite document analysis'));
    }

    return response.json();
  };

  const getEdgeCiteDocumentAnalysisStatus = async (
    documentId: string,
    options: { versionId?: string; jobId?: string } = {}
  ): Promise<EdgeCiteAnalysisStatusResponse> => {
    const searchParams = new URLSearchParams();
    if (options.versionId) searchParams.append('versionId', options.versionId);
    if (options.jobId) searchParams.append('jobId', options.jobId);
    const qs = searchParams.toString();
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/edgecite-analysis-status${qs ? `?${qs}` : ''}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response, 'Failed to get EdgeCite document analysis status'));
    }

    return response.json();
  };

  // Get document processing status
  const getDocumentProcessingStatus = async (documentId: string): Promise<DocumentProcessingStatusResponse> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/processing-status`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get processing status: ${response.statusText}`);
    }

    return response.json();
  };

  // AI Tag Suggestions
  const getAITagSuggestions = async (title: string, content?: string): Promise<{ suggestions: AITagSuggestion[] }> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/ai/tag-suggestions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title, content }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get AI tag suggestions: ${response.statusText}`);
    }

    return response.json();
  };

  // AI Related Documents
  const getAIRelatedDocuments = async (documentId: string): Promise<{ relatedDocuments: AIRelatedDocument[] }> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/related`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get related documents: ${response.statusText}`);
    }

    return response.json();
  };

  // AI Confidence Preview
  const getAIConfidencePreview = async (file: File, profile: string): Promise<AIConfidencePreview> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('profile', profile);

    const response = await fetch(`${baseUrl}/api/v1/dms/ai/confidence-preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to get AI confidence preview: ${response.statusText}`);
    }

    return response.json();
  };

  // MCP Processing Profiles
  const getMCPProcessingProfiles = async (): Promise<{ profiles: MCPProcessingProfile[] }> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/mcp/processing-profiles`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get MCP processing profiles: ${response.statusText}`);
    }

    return response.json();
  };

  // Jurisdictions
  const getJurisdictions = async (): Promise<{ jurisdictions: Jurisdiction[] }> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/jurisdictions`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get jurisdictions: ${response.statusText}`);
    }

    return response.json();
  };

  // Document Purposes
  const getDocumentPurposes = async (): Promise<{ purposes: DocumentPurpose[] }> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/document-purposes`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get document purposes: ${response.statusText}`);
    }

    return response.json();
  };

  const ingestDocumentUpload = async (
    file: File,
    payload: {
      intent: 'analyze' | 'ingest';
      title?: string;
      description?: string;
      clientId?: string;
      matterId?: string;
      categoryId?: string | null;
      categoryLabel?: string;
      primaryType?: string;
      subType?: string;
      documentPhase?: string;
      filingStatus?: string;
      sourceRole?: string;
      documentDate?: string;
      filedDate?: string;
      servedDate?: string;
      receivedDate?: string;
      tags?: string[];
      version?: string;
    }
  ): Promise<DocumentIngestAnalysis | DocumentIngestResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('intent', payload.intent);
    if (payload.title) formData.append('title', payload.title);
    if (payload.description) formData.append('description', payload.description);
    if (payload.clientId) formData.append('clientId', payload.clientId);
    if (payload.matterId) formData.append('matterId', payload.matterId);
    if (payload.categoryId) formData.append('categoryId', payload.categoryId);
    if (payload.categoryLabel) formData.append('categoryLabel', payload.categoryLabel);
    if (payload.primaryType) formData.append('primaryType', payload.primaryType);
    if (payload.subType) formData.append('subType', payload.subType);
    if (payload.documentPhase) formData.append('documentPhase', payload.documentPhase);
    if (payload.filingStatus) formData.append('filingStatus', payload.filingStatus);
    if (payload.sourceRole) formData.append('sourceRole', payload.sourceRole);
    if (payload.documentDate) formData.append('documentDate', payload.documentDate);
    if (payload.filedDate) formData.append('filedDate', payload.filedDate);
    if (payload.servedDate) formData.append('servedDate', payload.servedDate);
    if (payload.receivedDate) formData.append('receivedDate', payload.receivedDate);
    if (payload.tags && payload.tags.length > 0) formData.append('tags', JSON.stringify(payload.tags));
    if (payload.version) {
      formData.append('version', payload.version);
      formData.append('initial_version_number', payload.version);
    }

    const response = await fetch(`${baseUrl}/api/v1/dms/uploads/ingest`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response, 'Failed to ingest document upload'));
    }

    return response.json();
  };

  // Enhanced Document Creation
  const createEnhancedDocument = async (documentData: EnhancedDocumentFormData): Promise<DocumentDetail> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/enhanced?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(documentData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create enhanced document: ${response.statusText}`);
    }

    return response.json();
  };

  // Multi-file Upload
  const uploadMultipleDocuments = async (files: File[], baseMetadata: Partial<EnhancedDocumentFormData>): Promise<DocumentDetail[]> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append(`files`, file);
    });
    formData.append('metadata', JSON.stringify(baseMetadata));

    const response = await fetch(`${baseUrl}/api/v1/dms/documents/multi-upload?realmId=${realmId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload multiple documents: ${response.statusText}`);
    }

    return response.json();
  };

  // Get document versions with new response shape
  const getDocumentVersions = async (documentId: string, paramRealmId?: string): Promise<DocumentVersionsResponse> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document versions: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.versions) {
      return data as DocumentVersionsResponse;
    } else {
      // Fallback for old response format
      return {
        versions: Array.isArray(data) ? data : [],
        total: Array.isArray(data) ? data.length : 0,
        source_document_id: documentId
      };
    }
  };

  // Create new document version with new response shape
  const createDocumentVersion = async (documentId: string, versionData: any, paramRealmId?: string): Promise<DocumentVersionResponse> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(versionData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create document version: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.version) {
      return data as DocumentVersionResponse;
    } else {
      // Fallback for old response format
      return { version: data as DocumentVersion };
    }
  };

  // Update document metadata with new response shape
  const updateDocument = async (documentId: string, updates: Partial<DocumentDetail>, paramRealmId?: string): Promise<DocumentDetail> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}?realmId=${realmId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update document: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.documents && data.documents.length > 0) {
      return data.documents[0] as DocumentDetail;
    } else {
      // Fallback for old response format
      return data as DocumentDetail;
    }
  };

  // Create new document with new response shape
  const createDocument = async (documentData: any, paramRealmId?: string): Promise<DocumentDetail> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(documentData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create document: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.documents && data.documents.length > 0) {
      return data.documents[0] as DocumentDetail;
    } else {
      // Fallback for old response format
      return data as DocumentDetail;
    }
  };

  // Create document + first version with AST (atomic)
  const createDocumentWithAst = async (
    payload: {
      title: string;
      uploadedFile?: any;
      initial_version_number?: string;
      ast: any;
      ast_version?: string;
      client_id?: string;
      matter_id?: string;
      description?: string;
    },
    paramRealmId?: string
  ): Promise<DocumentDetail> => {
    checkRealmId(paramRealmId);
    const body = { ...payload, ast_version: payload.ast_version || 'v1' };
    const response = await fetch(`${baseUrl}/api/v1/dms/documents?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`Failed to create document (with AST): ${response.statusText}`);
    }
    const data = await response.json();
    if (data.documents && data.documents.length > 0) {
      return data.documents[0] as DocumentDetail;
    }
    return data as DocumentDetail;
  };

  // Delete document with new response shape
  const deleteDocument = async (documentId: string, paramRealmId?: string): Promise<{ success: boolean; message: string }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}?realmId=${realmId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete document: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.success !== undefined) {
      return data as { success: boolean; message: string };
    } else {
      // Fallback for old response format
      return { success: true, message: 'Document deleted successfully' };
    }
  };

  // Get document extracted text content for editing
  const getDocumentContent = async (documentId: string, paramRealmId?: string): Promise<{ text: string; metadata?: any }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/content?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document content: ${response.statusText}`);
    }

    return response.json();
  };

  // Save document content (create new version)
  const saveDocumentContent = async (documentId: string, content: string, description?: string, paramRealmId?: string): Promise<DocumentVersion> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        content,
        changes_description: description || 'Updated via EdgeDocs editor',
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save document content: ${response.statusText}`);
    }

    return response.json();
  };

  // Create a new version with AST (atomic)
  const createDocumentVersionWithAst = async (
    documentId: string,
    payload: {
      version_number: string;
      uploadedFile?: any;
      changes_description?: string;
      ast: any;
      ast_version?: string;
      idempotency_key?: string;
    },
    paramRealmId?: string
  ): Promise<{ version: DocumentVersion }> => {
    checkRealmId(paramRealmId);
    const body = { ...payload, ast_version: payload.ast_version || 'v1' };
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to create version (with AST): ${response.status} ${text}`);
    }
    return response.json();
  };

  // Get AST for a specific version
  const getVersionAst = async (
    documentId: string,
    versionId: string,
    paramRealmId?: string
  ): Promise<{ ast: any; ast_version?: string }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/ast?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to fetch version AST: ${response.status} ${text}`);
    }

    return response.json();
  };

  // Save AST for an existing version (backfill/repair)
  const saveVersionAst = async (
    documentId: string,
    versionId: string,
    ast: any,
    astVersion: string = 'v1',
    paramRealmId?: string
  ): Promise<{ success: boolean; document_id: string; version_id: string; astHash: string }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/ast?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ast, ast_version: astVersion })
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to save version AST: ${response.status} ${text}`);
    }
    return response.json();
  };

  // Update document metadata
  const updateDocumentMetadata = async (documentId: string, updates: Partial<DocumentDetail>, paramRealmId?: string): Promise<DocumentDetail> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}?realmId=${realmId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update document metadata: ${response.statusText}`);
    }

    return response.json();
  };

  // Delete document (soft delete)
  const deleteDocumentSoft = async (documentId: string, paramRealmId?: string): Promise<void> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}?realmId=${realmId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete document: ${response.statusText}`);
    }
  };

  // Get document version download URL
  const getDocumentDownloadUrl = (documentId: string, versionId: string): string => {
    return `${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/download?realmId=${realmId}`;
  };

  // Get document version preview URL (inline preview)
  const getDocumentPreviewUrl = (documentId: string, versionId: string): string => {
    return `${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/preview?realmId=${realmId}`;
  };

  // Download document as blob (for viewing/editing)
  const downloadDocumentBlob = async (documentId: string, versionId: string, paramRealmId?: string): Promise<Blob> => {
    checkRealmId(paramRealmId);
    const response = await fetch(getDocumentDownloadUrl(documentId, versionId), {
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.statusText}`);
    }

    return response.blob();
  };

  // Download document preview as blob (office docs converted to PDF)
  const downloadDocumentPreviewBlob = async (documentId: string, versionId: string, paramRealmId?: string): Promise<Blob> => {
    checkRealmId(paramRealmId);
    const response = await fetch(getDocumentPreviewUrl(documentId, versionId), {
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to fetch document preview: ${response.status} ${text}`);
    }

    return response.blob();
  };

  const getDocumentVersionCitations = async (
    documentId: string,
    versionId: string,
    paramRealmId?: string,
  ): Promise<DocumentVersionCitationsResponse> => {
    checkRealmId(paramRealmId);
    const response = await fetch(
      `${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/citations?realmId=${realmId}`,
      { headers: getAuthHeaders() },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to fetch document citations: ${response.status} ${text}`);
    }

    return response.json();
  };

  // --- UX Optimization and Analytics Methods ---

  // Get document preview URL
  const getDocumentPreview = async (documentId: string, paramRealmId?: string): Promise<{
    preview: {
      document_id: string;
      preview_url: string;
      expires_at?: string;
      thumbnail_url?: string;
      preview_type: 'image' | 'pdf' | 'html';
    };
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/preview?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get document preview: ${response.statusText}`);
    }

    return response.json();
  };

  // Get document edit history
  const getDocumentEditHistory = async (documentId: string, paramRealmId?: string): Promise<{
    edit_history: any[];
    total: number;
    document_id: string;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/edit-history?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get document edit history: ${response.statusText}`);
    }

    return response.json();
  };

  // Manage document tags
  const getDocumentTags = async (
    documentId: string,
    paramRealmId?: string
  ): Promise<{
    success: boolean;
    document_id: string;
    document_tags: string[];
    updated_at: string;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/tags?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response, 'Failed to get document tags'));
    }

    return response.json();
  };

  const manageDocumentTags = async (
    documentId: string,
    action: 'add' | 'remove' | 'replace',
    tags: string[],
    paramRealmId?: string
  ): Promise<{
    success: boolean;
    document_tags: string[];
    updated_at: string;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/tags?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ action, tags }),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response, 'Failed to manage document tags'));
    }

    return response.json();
  };

  // Set external reference
  const setExternalReference = async (
    documentId: string,
    externalReferenceId: string,
    externalSystem: 'docusign' | 'clio' | 'custom',
    metadata?: any,
    paramRealmId?: string
  ): Promise<{
    success: boolean;
    external_reference_id: string;
    external_system: string;
    linked_at: string;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/external-reference?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ external_reference_id: externalReferenceId, external_system: externalSystem, metadata }),
    });

    if (!response.ok) {
      throw new Error(`Failed to set external reference: ${response.statusText}`);
    }

    return response.json();
  };

  // Track document access
  const trackDocumentAccess = async (
    documentId: string,
    accessType: 'view' | 'edit' | 'download' | 'share',
    sessionId?: string,
    paramRealmId?: string,
    versionId?: string
  ): Promise<{
    success: boolean;
    last_accessed_at: string;
    access_count: number;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/access-track?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ access_type: accessType, session_id: sessionId, version_id: versionId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to track document access: ${response.statusText}`);
    }

    return response.json();
  };

  // --- Lexical Content Methods ---

  // Get Lexical content
  const getLexicalContent = async (documentId: string, paramRealmId?: string): Promise<{
    lexical_content: {
      document_id: string;
      content: string;
      version_id: string;
      last_modified: string;
      collaboration_session?: string;
    };
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/lexical-content?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get Lexical content: ${response.statusText}`);
    }

    return response.json();
  };

  // Save Lexical content
  const saveLexicalContent = async (
    documentId: string,
    content: string,
    versionDescription?: string,
    collaborationSession?: string,
    paramRealmId?: string,
    saveType: 'auto' | 'manual' | 'version' = 'version',
    ast?: any,
    astVersion?: string,
    versionId?: string  // 🎯 ADD VERSION CONTEXT: Specify which version this represents
  ): Promise<{
    success: boolean;
    version_id: string;
    saved_at: string;
    collaboration_updated: boolean;
    was_new_version_created: boolean;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/lexical-content?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        content,
        version_description: versionDescription,
        collaboration_session: collaborationSession,
        save_type: saveType,
        ...(ast ? { ast } : {}),
        ...(astVersion ? { ast_version: astVersion } : {}),
        ...(versionId ? { version_id: versionId } : {}),  // 🎯 SEND VERSION CONTEXT TO BACKEND
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save Lexical content: ${response.statusText}`);
    }

    return response.json();
  };

  // Lock active version for pending version creation
  const lockActiveVersion = async (
    documentId: string,
    paramRealmId?: string
  ): Promise<{
    success: boolean;
    lockedVersionId: string;
    message: string;
  }> => {
    checkRealmId(paramRealmId);
    
    try {
      console.log('🔒 [DocumentAPI] lockActiveVersion called for document:', documentId);
      
      const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/lock-active-version?realmId=${realmId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });

      console.log('🔒 [DocumentAPI] lockActiveVersion response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [DocumentAPI] lockActiveVersion failed:', response.status, errorText);
        throw new Error(`Failed to lock active version: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('🔒 [DocumentAPI] lockActiveVersion response text:', responseText);
      
      if (!responseText) {
        throw new Error('Empty response from lock active version endpoint');
      }

      try {
        const result = JSON.parse(responseText);
        console.log('🔒 [DocumentAPI] lockActiveVersion parsed result:', result);
        return result;
      } catch (parseError) {
        console.error('❌ [DocumentAPI] lockActiveVersion JSON parse error:', parseError, 'Response text:', responseText);
        throw new Error(`Invalid JSON response from lock active version: ${responseText}`);
      }
      
    } catch (error) {
      console.error('❌ [DocumentAPI] lockActiveVersion error:', error);
      throw error;
    }
  };

  // Unlock version (cancel version creation)
  const unlockVersion = async (
    documentId: string,
    versionId: string,
    paramRealmId?: string
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/unlock?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`Failed to unlock version: ${response.statusText}`);
    }

    return response.json();
  };

  // Get content for a specific version
  const getVersionContent = async (
    documentId: string,
    versionId: string,
    paramRealmId?: string
  ): Promise<{
    version_content: {
      document_id: string;
      version_id: string;
      content: string;
      version_number: string;
      last_modified: string;
    };
  }> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/content?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get version content: ${response.statusText}`);
    }

    return response.json();
  };

  // Promote version to latest
  const promoteVersion = async (
    documentId: string,
    versionId: string,
    paramRealmId?: string
  ): Promise<DocumentDetail> => {
    checkRealmId(paramRealmId);
    const response = await fetch(`${baseUrl}/api/v1/dms/documents/${documentId}/versions/${versionId}/promote?realmId=${realmId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to promote version: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    // Handle new type-specific response shape
    if (data.document) {
      return data.document as DocumentDetail;
    } else {
      // Fallback for old response format
      return data as DocumentDetail;
    }
  };

  // Compare versions
  const compareVersions = async (
    documentId: string,
    versionA: string,
    versionB: string,
    options: { includeAi?: boolean } = {}
  ): Promise<any> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/${documentId}/versions/compare`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          baseVersionId: versionA,
          comparisonVersionId: versionB,
          includeAi: options.includeAi !== false,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to compare versions: ${response.status} - ${errorText || response.statusText}`);
    }

    return response.json();
  };

  // ============================================================================
  // CHECK-IN/CHECK-OUT WORKFLOW API METHODS (Phase 3 - Legal DMS Enhancement)
  // ============================================================================

  /**
   * Check out a document for editing
   */
  const checkoutDocument = async (
    documentId: string,
    options: {
      durationHours?: number;
      timeoutHours?: number;
      sessionId?: string;
      deviceInfo?: string;
    } = {}
  ): Promise<{
    success: boolean;
    document: {
      document_id: string;
      title: string;
      is_checked_out: boolean;
      checked_out_by_user_id: string | null;
      checked_out_at: string | null;
      checked_out_expires_at: string | null;
    };
    message: string;
  }> => {
    const durationHours = options.durationHours ?? options.timeoutHours ?? 24;
    const payload = {
      durationHours,
      sessionId: options.sessionId,
      deviceInfo: options.deviceInfo,
    };

    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/${documentId}/checkout`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to checkout document: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Check in a document with optional version creation
   */
  const checkinDocument = async (
    documentId: string,
    options: {
      createNewVersion: boolean;
      versionComment?: string;
      forceCheckin?: boolean;
      forceCheckinReason?: string;
      workingCopyData?: {
        content?: string;
        filePath?: string;
        metadata?: {
          sessionId?: string;
          deviceInfo?: string;
          autosaveCount?: number;
        };
      };
    }
  ): Promise<{
    success: boolean;
    document: {
      document_id: string;
      title: string;
      is_checked_out: boolean;
      latest_version_id: string | null;
    };
    message: string;
  }> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/${documentId}/checkin`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to checkin document: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Force check-in by admin
   */
  const forceCheckinDocument = async (
    documentId: string,
    reason: string
  ): Promise<{
    success: boolean;
    document: {
      document_id: string;
      title: string;
      is_checked_out: boolean;
    };
    message: string;
  }> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/${documentId}/force-checkin`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to force checkin document: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Save working copy (for autosave)
   */
  const saveWorkingCopy = async (
    documentId: string,
    workingCopyData: {
      content?: string;
      filePath?: string;
      metadata?: {
        sessionId?: string;
        deviceInfo?: string;
        autosaveCount?: number;
      };
    }
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/${documentId}/save-working-copy`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workingCopyData),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to save working copy: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Get checkout status
   */
  const getCheckoutStatus = async (
    documentId: string
  ): Promise<{
    success: boolean;
    checkoutStatus: {
      isCheckedOut: boolean;
      checkedOutBy: any;
      checkedOutAt: string | null;
      checkedOutExpiresAt: string | null;
      hasWorkingCopy: boolean;
      lastLocalSaveAt: string | null;
      workingCopyMetadata: any;
    };
  }> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/${documentId}/checkout-status`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get checkout status: ${response.statusText}`);
    }

    return response.json();
  };

  // ============================================================================
  // VERSION-SPECIFIC CHECKOUT METHODS
  // ============================================================================

  /**
   * Check out a specific document version
   */
  const checkoutDocumentVersion = async (
    versionId: string,
    options: {
      durationHours?: number;
      timeoutHours?: number;
    } = {}
  ): Promise<{
    success: boolean;
    message: string;
    expiresAt: string;
  }> => {
    const durationHours = options.durationHours ?? options.timeoutHours ?? 24;
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/versions/${versionId}/checkout`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          durationHours
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to checkout document version: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Check in a specific document version
   */
  const checkinDocumentVersion = async (
    versionId: string,
    options: {
      createNewVersion?: boolean;
      versionComment?: string;
      forceCheckin?: boolean;
      forceCheckinReason?: string;
      checkinMessage?: string;
      sessionId?: string;
      deviceInfo?: string;
      workingCopyData?: {
        content?: string;
        filePath?: string;
        metadata?: {
          sessionId?: string;
          deviceInfo?: string;
          autosaveCount?: number;
        };
      };
    } = {}
  ): Promise<{
    success: boolean;
    message: string;
    newVersionId?: string;
  }> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/versions/${versionId}/checkin`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to checkin document version: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Get checkout status for a specific document version
   */
  const getVersionCheckoutStatus = async (
    versionId: string
  ): Promise<{
    success: boolean;
    checkoutStatus: {
      isCheckedOut: boolean;
      checkedOutBy: any;
      checkedOutAt: string | null;
      checkedOutExpiresAt: string | null;
      hasWorkingCopy: boolean;
      lastLocalSaveAt: string | null;
      workingCopyMetadata: any;
    };
  }> => {
    const response = await fetch(
      `${window.location.origin}/api/v1/dms/documents/versions/${versionId}/checkout-status`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get version checkout status: ${response.statusText}`);
    }

    return response.json();
  };

  // ============================================================================
  // END CHECK-IN/CHECK-OUT WORKFLOW API METHODS
  // ============================================================================

  return useMemo(() => ({
    // Core CRUD operations
    listDocuments,
    getDocument,
    createDocument,
    createDocumentWithAst,
    updateDocument,
    deleteDocument,
    
    // Document versions
    getDocumentVersions,
    createDocumentVersion,
    createDocumentVersionWithAst,
    promoteVersion,
    
    // Content operations
    getDocumentContent,
    saveDocumentContent,
    updateDocumentMetadata,
    
    // Download operations
    getDocumentDownloadUrl,
    downloadDocumentBlob,
    getDocumentPreviewUrl,
    downloadDocumentPreviewBlob,
    getDocumentVersionCitations,
    
    // Search and filtering
    searchDocuments,
    filterDocuments,
    getDMSFilterOptions,
    
    // Analytics
    getDocumentAnalytics,
    getComprehensiveAnalyticsReport,
    getRealTimeAnalytics,
    getAnalyticsDashboardSummary,
    exportAnalyticsReport,
    
    // AI Processing
    processDocument,
    triggerEdgeCiteDocumentAnalysis,
    getEdgeCiteDocumentAnalysisStatus,
    getDocumentProcessingStatus,
    
    // UX Optimization and Analytics
    getDocumentPreview,
    getDocumentEditHistory,
    getDocumentTags,
    manageDocumentTags,
    setExternalReference,
    trackDocumentAccess,
    
    // Lexical Integration
    getLexicalContent,
    saveLexicalContent,
    lockActiveVersion,
    unlockVersion,
    getVersionContent,
    compareVersions,
    // AST helpers
    getVersionAst,
    saveVersionAst,
    
    // AI Enhancement Methods
    getAITagSuggestions,
    getAIRelatedDocuments,
    getAIConfidencePreview,
    getMCPProcessingProfiles,
    getJurisdictions,
    getDocumentPurposes,
    ingestDocumentUpload,
    createEnhancedDocument,
    uploadMultipleDocuments,
    
    // Check-in/Check-out Workflow Methods
    checkoutDocument,
    checkinDocument,
    forceCheckinDocument,
    saveWorkingCopy,
    getCheckoutStatus,
    
    // Version-Specific Checkout Methods
    checkoutDocumentVersion,
    checkinDocumentVersion,
    getVersionCheckoutStatus,
    
    // Legacy methods for backward compatibility
    deleteDocumentSoft,
    
    // Utility methods for external API calls
    getBaseUrl: () => baseUrl,
    getAuthHeaders,
    
    realmId, // export for use in UI if needed
  }), [keycloak.token, realmId]); // Stable dependencies
};

// Export default API instance
export const DocumentAPI = {
  useDocumentAPI,
};
